import type { Messages } from '../messages.js'

/**
 * Slovene. `poteza` for flips, the word a Slovene board game uses for a move.
 *
 * Slovene has a dual, which almost nothing else here does: two words is `dve besedi`, and it is
 * a form of its own rather than a plural used loosely. Getting it wrong is not a subtle error.
 */
export const sl: Messages = {
  tag: 'sl',

  readingDictionary: 'Berem slovar…',
  noWordList: 'Za »{language}« ni seznama besed. Ustvarite ga:  pnpm dictionary build',
  emptyWordList: 'Seznam besed za »{language}« je prazen.',

  flips: 'poteze',
  score: 'rezultat',
  words: 'besede',
  round: 'krog',
  ticksLeftLabel: 'Preostali čas v tem krogu',
  typeAWord: 'vpišite besedo',
  tapPrompt: 'tapnite črke za izbiro ali vrnitev, nato {action}',

  boardOfTiles: 'Igralna plošča s {n} ploščicami',
  faceDown: 'obrnjeno navzdol',
  wildCard: 'joker',
  wildKey: 'katera koli črka',
  letterReplaced: '{from} se je spremenil v {to}',
  letterSwap: 'ZAMENJAVA ČRK!',
  spentTile: 'porabljena ploščica',
  hiddenWhilePaused: 'skrito med premorom',
  letterInWord: '{letter}, {position}. črka besede',

  completeWord: 'Dokončaj besedo',

  completeShort: 'Dokončaj',
  reset: 'Počisti',
  pause: 'Premor',
  resume: 'Nadaljuj',
  newGame: 'Nova igra',
  paused: 'Ustavljeno',
  outOfFlips: 'Zmanjkalo je potez',
  finalResult: '{score} za {words} v {rounds}',
  playAgain: 'Igraj znova',
  share: 'Deli',
  shareCopied: 'Kopirano.',
  shareSelect: 'Kopirajte to:',

  lettersSelect: 'črke izbirajo',
  keysWild: 'vzame se, ko vpišete črko, ki je ni na plošči',
  clearsEvery: 'počisti vse izbrane {letter}',
  undoLastLetter: 'razveljavi zadnjo črko',
  noWordsYet: 'Zaenkrat ni besed.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'že najdeno',
  reasonTooShort: 'prekratko',
  reasonNotAWord: 'ni beseda',
  reasonAllFound: 'vse jih že imate',
  noSuchLetterUp: 'ni odkritega {letter}',
  nothingUp: 'nič ni odkrito',
  letterHidden: 'ena črka se je skrila',
  shuffled: 'premešano',
  shuffledAndBilled: 'premešano, zaračunanih {flips} neporabljenih',
  tooFewLetters: 'Ostalo je premalo črk',

  gameLanguage: 'jezik',
  interfaceLanguage: 'vmesnik',
  dictionarySize: '{common} vsakdanjih od {full} besed',
  filterLanguages: 'Iskanje jezika',
  noMatches: 'Ni zadetkov',

  nerdMode: 'način za navdušence',
  rules: 'Pravila',
  difficulty: 'težavnost',
  difficultyNames: { easy: 'lahka', medium: 'srednja', hard: 'težka', insane: 'nora' },
  pausedNoBoard: 'Ta igra ne bo prišla na lestvico.',
  about: 'O igri',
  themeLabel: 'tema',
  themeNames: { traditional: 'tradicionalna', light: 'svetla', contrast: 'visok kontrast' },
  tiles: 'ploščice (N)',
  secondsPerTick: 'sekunde / utrip',
  holdTicks: 'utripi zadržanja',
  minWord: 'najkrajša beseda',
  startingFlips: 'začetne poteze',
  wildChance: 'možnost jokerja',
  replaceChance: 'možnost zamenjave črk',
  hideChance: 'možnost skritja črke',
  wordCompleteMode: 'ko je beseda končana',
  wordCompleteNames: { shuffle: 'premešaj', spend: 'porabi', keep: 'obdrži' },
  flipEconomy: 'vračanje potez',
  flipEconomyNames: {
    none: 'brez',
    perLetter: 'na črko',
    fibonacci: 'fibonacci',
    overMinimum: 'nad najmanjšo',
  },
  repeatedLetterKey: 'tipka ponovljene črke',
  keySchemeNames: { cycle: 'krožno', advance: 'naprej' },
  keySchemeHelp: {
    cycle:
      'A vzame naslednji neuporabljeni A, in ko so vsi v besedi, jih počisti. ' +
      'Shift+A jih tudi počisti.',
    advance: 'A vzame naslednji neuporabljeni A. Shift+A počisti vse A v besedi.',
  },

  whatThatMeans: 'Kaj to pomeni',
  factRound: 'krog',
  factWholeBoardUp: 'vsa plošča odkrita za',
  factRoundCosts: 'krog stane',
  factFlipsBuy: 'začetne poteze kupijo',
  factThisBoard: 'ta plošča',
  factBoardHadToAdmit: 'plošča je morala dopustiti',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, najdaljša {longest}',
  wordsIncludingOneOf: '{words}, med njimi ena od {ceiling}',
  scorelessRounds: '{rounds} brez točk',

  whatAWordPays: 'Kaj prinese beseda',
  columnLetters: 'črke',
  columnCost: 'cena',
  columnPoints: 'točke',
  columnFlips: 'poteze',
  columnNet: 'razlika',

  canonicalRules: 'Standardna pravila za {difficulty}.',
  customRules: 'Spremenjeno od prednastavitve. Rezultati po lastnih pravilih se ne uvrščajo.',
  applyAndStart: 'Uporabi in začni novo igro',
  changesNextGame: 'Spremembe začnejo veljati z naslednjo igro.',
  presets: 'Prednastavitve:',

  start: 'Začni',
  restart: 'Znova',
  quit: 'Končaj',
  quitTitle: 'Končati to igro?',
  restartTitle: 'Začeti to igro znova?',
  restartConfirm: 'Znova',
  quitConfirm: 'Končaj',
  keepPlaying: 'Igraj naprej',
  personalBest: 'Vaše najboljše igre',
  newPersonalBest: 'Nov osebni rekord.',
  columnRank: '#',
  notRanked: 'Lastna pravila, zato se ta igra ne uvršča.',
  notRankedPaused: 'Ura se je ustavila, zato se ta igra ne uvršča.',
  rankOfTotal: '{rank} od {total}',

  howToPlay: 'Kako igrati',

  backToGame: 'Nazaj v igro',
  welcomeTitle: 'Dobrodošli v Blinkered',
  tutorialSkip: 'Preskoči',
  tutorialNext: 'Naprej',
  tutorialBack: 'Nazaj',
  tutorialStart: 'Začni igrati',
  tutorialHideAgain: 'Tega ne kaži več',
  tutorialProgress: '{n} od {total}',
  tutorialSkipTitle: 'Preskočiti uvod?',
  tutGoalTitle: 'Blinkered je dirka',
  tutGoalBody:
    'Igro začnete z določenim številom potez, vsaka odkrita ploščica pa stane eno potezo. Ko potez zmanjka, je igre konec. Besede prinašajo poteze, in daljša kot je beseda, več potez dobite.',
  tutSpentBody:
    'Črke, ki jih uporabite v besedi, ostanejo obrnjene do naslednjega kroga. Katere črke so na plošči, spremeni le menjava črke.',
  tutChangeTitle: 'Črke se lahko spreminjajo',
  tutSameLetters:
    'Ste opazili besedo, ki je niste utegnili odigrati? Iste črke se vrnejo v naslednjem krogu, premešane na nova mesta.',
  tutPickLetters: 'Tapkajte črke po vrsti, da sestavite besedo.',
  tutMoreTurn: 'Ploščice se odkrivajo, medtem ko razmišljate, zato boljša črka morda še pride.',
  tutTapBack:
    'Ste vzeli črko, ki je niste želeli? Tapnite jo znova in se vrne. Katero koli, ne le zadnjo.',
  tutComplete: 'Ko je beseda pripravljena, pritisnite Dokončaj.',
  htBoardTitle: 'Plošča',
  htBoardBody:
    'Ploščice se odkrivajo ena za drugo, v naključnem vrstnem redu. Iz odkritih se sestavljajo besede.',
  htWordsTitle: 'Besede',
  htWordsBody: 'Besedo sestavite iz odkritih ploščic z vpisovanjem ali klikanjem črk po vrsti.',
  htFlipsTitle: 'Poteze',
  htFlipsBody:
    'Vsaka odkrita ploščica stane potezo. Dokončana beseda poteze vrne, daljše besede pa vrnejo več. Ko potez zmanjka, je igre konec.',
  htRoundTitle: 'Krog',
  htRoundBody:
    'Ko se odkrije zadnja ploščica kroga, vsa plošča za trenutek obstane. Nato se ploščice obrnejo in premešajo in začne se nov krog.',
  htLanguagesTitle: 'Jeziki',
  htLanguagesBody:
    'Toliko jih je: {n}. Vsako ploščo je mogoče rešiti z besedami, ki jih ljudje res uporabljajo. Nenavadna beseda šteje tudi, če jo slovar pozna.',
  htKeysTitle: 'Tipkovnica',
  htWildTitle: 'Jokerji',
  htWildBody:
    'Včasih se namesto črke pojavi joker. Joker velja za katero koli črko, ki tvori veljavno besedo. Že dokončana beseda ne šteje.',
  htHideTitle: 'Črke, ki se skrijejo',
  htHideBody:
    'Na težjih stopnjah se odkrita črka lahko sama obrne nazaj in se vrne pozneje v krogu. Časovnik za to dobi dodatno črtico, rdečo. Že izbrane črke se ne obrnejo nazaj.',
  tutHideBody:
    'Na težjih stopnjah se odkrita črka lahko sama obrne nazaj in se vrne pozneje v krogu. Že izbrane črke se ne obrnejo nazaj.',
  htSwapTitle: 'Menjava črk',
  htSwapBody:
    'Včasih se med krogi ena črka zamenja z drugo. Videli boste, katera je odšla in katera je prišla.',
  htLevelsTitle: 'Težavnosti',
  htLevelEasy:
    'Istih dvanajst črk vso igro, tako da se jih lahko naučite in nosite seznam besed v glavi. Ploščice se odkrivajo počasi, vsa plošča pa ostane vidna dovolj dolgo, da dokončate izbiro.',
  htLevelMedium:
    'Tu in tam se črka spremeni, zato si je težje zapomniti besede, prihranjene za pozneje. Manj časa za gledanje in manj za razmišljanje.',
  htLevelHard:
    'Tričrkovne besede nehajo šteti, črka pa se spremeni približno vsak drugi krog. Plošča se komaj pokaže, že se premeša.',
  htLevelInsane:
    'Vse hkrati in s polno hitrostjo. Plošča se premeša skoraj takoj po zadnji potezi.',
  htTouchTitle: 'Zaslon na dotik',
  htTouchBody:
    'Tapnite odkrito ploščico, da vzamete njeno črko. Tapnite vzeto črko, da jo vrnete. Dokončaj in Počisti sta pod ploščo.',

  accountTitle: 'Tvoj račun',
  menuProfile: 'Moj profil',
  menuGames: 'Moje igre',
  menuPublicPage: 'Moja javna stran',
  menuSignedInAs: 'Prijavljen kot',
  signIn: 'Prijava',
  signOut: 'Odjava',
  signInTitle: 'Prijavi se ali ustvari račun',
  signInLead: 'Račun shrani tvoje igre, na vseh napravah.',
  signInKeepGame: 'Prijavi se, da ohraniš to igro, in vse naslednje.',
  continueWithApple: 'Nadaljuj z Apple',
  continueWithGoogle: 'Nadaljuj z Google',
  signInOr: 'ali',
  emailLabel: 'E-pošta',
  codeLabel: 'Koda',
  emailMeACode: 'Pošljite mi kodo',
  working: 'Trenutek…',
  sendNewCode: 'Pošlji novo kodo',
  codeSent: 'Poglej v e-pošto. Koda velja 10 minut.',
  notNow: 'Ne zdaj',
  badEmail: 'To ni videti kot e-poštni naslov.',
  badCode: 'Ta koda ni delovala. Kode potečejo po 10 minutah in veljajo enkrat.',
  serverBusy: 'Strežnik ni dosegljiv. Poskusi čez trenutek.',
  serverDown: 'Strežnik ni dosegljiv.',
  offline: 'Brez povezave',
  leaderboardTitle: 'Lestvica',
  leaderboardEmpty: 'Na tej lestvici še ni iger. Bodi prvi.',
  leaderboardWouldBe: 'Kje bi bila ta igra',
  leaderboardThisGame: 'Ta igra',
  leaderboardRanksHere: 'Kje je ta igra',
  leaderboardOutside: 'Zunaj prve peterice.',
  tutAccountTitle: 'Shrani svoje rezultate',
  tutAccountBody: 'Račun shrani tvoje igre, uvrsti rezultate na lestvico in ohrani tvoj profil.',
  ssoCancelled: 'Prijava preklicana.',
  ssoExpired: 'Trajalo je predolgo. Prijavi se znova.',
  ssoNoUsername: 'Računa ni bilo mogoče dokončati. Poskusi čez trenutek.',
  ssoFailed: 'Ni šlo. Poskusi znova ali uporabi e-pošto.',
  tabProfile: 'Profil',
  countryAny: 'Raje ne povem',
  findCountry: 'Iskanje držav',
  usernameLabel: 'Uporabniško ime',
  bioLabel: 'O meni',
  countryLabel: 'Država',
  bioHint: 'Brez povezav. {left}/{max}',
  save: 'Shrani',
  saving: 'Shranjujem…',
  saved: 'Shranjeno.',
  nameTaken: 'To ime že ima nekdo.',
  nameTooShort: 'Malo daljše, prosim.',
  nameTooLong: 'To je predolgo.',
  nameBadCharacters: 'Samo črke, števke, vezaji in podčrtaji.',
  nameBadEdges: 'Začeti in končati se mora s črko ali števko.',
  nameMixedScripts: 'Izberi eno abecedo in ostani pri njej.',
  nameReserved: 'To je videti kot ime, ki ga podeljujemo mi. Izberi drugo.',
  nameUnusable: 'Tega imena ni mogoče uporabiti.',
  bioTooLong: 'Opis ima največ {max} znakov.',
  bioHasLink: 'Povezave v opisu niso dovoljene.',
  bioHasControl: 'Vsebuje znake, ki jih opis ne more imeti.',
  bioUnusable: 'Tega opisa ni mogoče uporabiti.',
  gamesHeading: 'Igre',
  gamesEmpty: 'Tu še ni ničesar. Igre, ki jih odigraš prijavljen, se pojavijo tukaj.',
  gamesEmptyShort: 'Tu še ni ničesar.',
  gamesLoading: 'Berem tvoje igre…',
  theirGamesLoading: 'Berem njihove igre…',
  profileLoading: 'Berem profil…',
  playerNotFound: 'Tu ni nikogar s tem imenom.',
  columnWhen: 'Kdaj',
  columnRounds: 'krogov',
  columnGame: 'Igra',
  gameLoading: 'Berem igro…',
  gameUnreadable: 'Te igre ni bilo mogoče prebrati.',
  gameNotRanked: 'Odigrana s spremenjenimi pravili, zato se ne uvršča.',
  detailNotKept: 'Besede in plošče te igre se ne hranijo več.',
  openingBoard: 'Začetna plošča',
  roundByRound: 'Krog za krogom',
  roundNothing: 'nič najdenega',
  changeReplaced: '{letter} zamenjana na mestu {slot}',
  changeWildOn: '{letter} joker na mestu {slot}',
  changeWildOff: '{letter} joker odstranjen z mesta {slot}',
  keepThisGame: 'Ohrani to igro',
  savedToYourGames: 'Shranjeno med tvoje igre.',

  reportThis: 'Prijavi',
  reportHeading: 'Prijavi to',
  reportTheName: 'Ime',
  reportTheBio: 'Opis',
  reportTheScore: 'Rezultat',
  reportWhy: 'Kaj je s tem narobe? Neobvezno.',
  reportSend: 'Pošlji prijavo',
  reportSent: 'Hvala. Nekdo si bo to ogledal.',
  reportSignIn: 'Za to moraš biti prijavljen.',
  reportGone: 'Tega ni več.',
  reportCancel: 'Prekliči',

  deleteAccount: 'Izbriši moj račun',
  deleteAccountWhat:
    'To za vedno odstrani tvoj profil in vse shranjene igre. Tega ni mogoče razveljaviti.',
  deleteAccountAsk: 'Pošljite mi kodo',
  deleteConfirm: 'Izbriši za vedno',
  deleteGone: 'Tvojega računa ni več. Hvala, da si igral.',
  deleteNoAddress:
    'Na tem računu ni potrjenega naslova, zato izbrisa ni mogoče preveriti. Javi se nam in naredili bomo ročno.',

  rulesetFacts: 'Besede z {letters} · nova ploščica na {seconds}',
  plurals: {
    words: { one: '{n} beseda', two: '{n} besedi', few: '{n} besede', other: '{n} besed' },
    rounds: { one: '{n} krog', two: '{n} kroga', few: '{n} krogi', other: '{n} krogov' },
    flips: { one: '{n} poteza', two: '{n} potezi', few: '{n} poteze', other: '{n} potez' },
    ticks: { one: '{n} utrip', two: '{n} utripa', few: '{n} utripi', other: '{n} utripov' },
    points: { one: '{n} točka', two: '{n} točki', few: '{n} točke', other: '{n} točk' },
    minLetters: {
      one: 'vsaj {n} črko',
      two: 'vsaj {n} črkama',
      few: 'vsaj {n} črkami',
      other: 'vsaj {n} črkami',
    },
    seconds: { one: '{n} sekundo', two: '{n} sekundi', few: '{n} sekunde', other: '{n} sekund' },
  },
}
